import React, { useState, useEffect } from 'react';
import { 
  Award, Leaf, CheckCircle, TrendingUp, Building2, Phone,
  ChevronDown, ArrowRight, BarChart3, Globe, Shield, FileCheck,
  Target, Sparkles, BadgeCheck, Trophy
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';

const HuellaCeroPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const benefits = [
    {
      icon: TrendingUp,
      title: 'Diferenciación Competitiva',
      description: 'Destacá tu negocio como líder en sustentabilidad frente a clientes y competidores.'
    },
    {
      icon: Target,
      title: 'Cumplimiento ESG',
      description: 'Cumplí con los estándares ambientales cada vez más exigidos por inversores y mercados.'
    },
    {
      icon: Globe,
      title: 'Marketing Verde Real',
      description: 'Comunicá tu compromiso ambiental con certificación verificable y transparente.'
    },
    {
      icon: Building2,
      title: 'Acceso a Mercados',
      description: 'Abrí puertas a clientes corporativos y licitaciones que exigen sustentabilidad.'
    }
  ];

  const certificationProcess = [
    {
      step: '01',
      title: 'Diagnóstico Energético',
      description: 'Medimos tu consumo actual y calculamos tu huella de carbono empresarial.'
    },
    {
      step: '02',
      title: 'Plan de Acción',
      description: 'Diseñamos estrategias para reducir y compensar tus emisiones de CO₂.'
    },
    {
      step: '03',
      title: 'Implementación Solar',
      description: 'Instalamos sistemas solares dimensionados para cubrir tu demanda energética.'
    },
    {
      step: '04',
      title: 'Verificación',
      description: 'Medimos y documentamos la reducción real de emisiones lograda.'
    },
    {
      step: '05',
      title: 'Certificación',
      description: 'Emitimos tu certificado oficial de "Huella Cero" o "Carbono Neutro".'
    }
  ];

  const certificationLevels = [
    {
      name: 'Huella Reducida',
      reduction: '50-75%',
      color: 'bg-amber-500',
      description: 'Para empresas en transición hacia la sustentabilidad.',
      includes: ['Certificado anual', 'Sello para materiales', 'Reporte de impacto', 'Auditoría básica']
    },
    {
      name: 'Huella Cero',
      reduction: '100%',
      color: 'bg-[#90EE90]',
      description: 'Cobertura total de tu consumo eléctrico con energía solar.',
      includes: ['Certificado anual', 'Sello premium', 'Reporte detallado', 'Auditoría completa', 'Marketing kit', 'Reconocimiento público'],
      highlighted: true
    },
    {
      name: 'Carbono Positivo',
      reduction: '100%+',
      color: 'bg-green-600',
      description: 'Generás más energía limpia de la que consumís.',
      includes: ['Todo de Huella Cero', 'Créditos de carbono', 'Contribución a la red', 'Impacto social certificado']
    }
  ];

  const faqs = [
    {
      question: '¿Qué significa ser "Huella Cero"?',
      answer: 'Significa que tu empresa compensa el 100% de las emisiones de CO₂ generadas por su consumo eléctrico mediante energía renovable. Es una certificación que demuestra tu compromiso real con el medio ambiente.'
    },
    {
      question: '¿Quién emite la certificación?',
      answer: 'La certificación es emitida por Zenex en base a mediciones reales de generación solar vs. consumo eléctrico. Nuestro proceso cumple con estándares internacionales de contabilidad de carbono.'
    },
    {
      question: '¿Cuánto tiempo dura la certificación?',
      answer: 'La certificación tiene validez de 1 año. Cada año realizamos una auditoría para verificar que se mantiene el nivel de compensación y renovamos el certificado.'
    },
    {
      question: '¿Puedo usar el sello en mi publicidad?',
      answer: 'Sí, al obtener la certificación recibes un kit completo de marketing que incluye el sello oficial, lineamientos de uso, y material gráfico para comunicar tu logro de forma profesional.'
    },
    {
      question: '¿Qué pasa si mi consumo aumenta?',
      answer: 'Si tu consumo crece, podemos ampliar tu sistema solar para mantener la cobertura. También existe la opción de compensar mediante otros mecanismos certificados.'
    },
    {
      question: '¿Esto aplica solo a electricidad?',
      answer: 'La certificación estándar cubre las emisiones por consumo eléctrico. Para una certificación completa que incluya otras fuentes de emisión (transporte, combustibles, etc.), ofrecemos programas personalizados.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation isScrolled={isScrolled} />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-green-900/30 to-zinc-900"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#90EE90] rounded-full filter blur-[100px]"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-green-400 rounded-full filter blur-[120px]"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#90EE90]/20 rounded-full mb-6">
              <Award className="w-4 h-4 text-[#90EE90]" />
              <span className="text-sm font-medium text-[#90EE90]">Certificación Ambiental</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Certificación
              <span className="text-[#90EE90]"> "Huella Cero"</span>
            </h1>
            
            <p className="text-base sm:text-lg md:text-xl text-zinc-300 mb-8 leading-relaxed">
              Demostrá el compromiso ambiental de tu empresa con una certificación oficial. 
              Diferenciáte en el mercado siendo carbono neutro.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20la%20certificación%20Huella%20Cero%20para%20mi%20empresa"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 px-8 py-4 bg-[#90EE90] hover:bg-[#7DD87D] text-zinc-900 font-bold rounded-xl transition-all duration-300 hover:scale-105"
              >
                Certificar mi Empresa
                <ArrowRight size={20} />
              </a>
              <button
                onClick={() => navigate('/calculadora')}
                className="flex items-center justify-center gap-2 px-8 py-4 bg-white/10 hover:bg-white/20 text-white font-bold rounded-xl transition-all duration-300 border border-white/20"
              >
                Calcular mi Huella
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Beneficios
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              ¿Por qué Certificarse?
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <div key={index} className="bg-white p-6 rounded-2xl border border-zinc-200 hover:shadow-xl transition-all duration-300">
                  <div className="p-3 bg-[#90EE90]/20 rounded-xl w-fit mb-4">
                    <Icon className="w-6 h-6 text-[#14532D]" />
                  </div>
                  <h3 className="text-lg font-bold text-zinc-900 mb-2">{benefit.title}</h3>
                  <p className="text-sm text-zinc-600">{benefit.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certification Levels */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Niveles de Certificación
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-4">
              Elegí tu Nivel de Compromiso
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {certificationLevels.map((level, index) => (
              <div 
                key={index} 
                className={`rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                  level.highlighted 
                    ? 'border-2 border-[#90EE90] shadow-xl' 
                    : 'border border-zinc-200'
                }`}
              >
                {level.highlighted && (
                  <div className="bg-[#90EE90] text-zinc-900 text-center py-2 text-sm font-bold">
                    MÁS POPULAR
                  </div>
                )}
                <div className="bg-white p-6">
                  <div className={`${level.color} w-12 h-12 rounded-full flex items-center justify-center mb-4`}>
                    <BadgeCheck className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-1">{level.name}</h3>
                  <p className="text-3xl font-bold text-[#14532D] mb-3">{level.reduction}</p>
                  <p className="text-sm text-zinc-600 mb-4">{level.description}</p>
                  
                  <ul className="space-y-2 mb-6">
                    {level.includes.map((item, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-600">
                        <CheckCircle className="w-4 h-4 text-[#90EE90] flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                  
                  <a
                    href="https://wa.me/595991344956?text=Hola%20Zenex!%20Me%20interesa%20la%20certificación%20de%20nivel%20${encodeURIComponent(level.name)}"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-full py-3 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                      level.highlighted
                        ? 'bg-[#90EE90] text-zinc-900 hover:bg-[#7CDF7C]'
                        : 'bg-zinc-100 text-zinc-900 hover:bg-zinc-200'
                    }`}
                  >
                    Consultar
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-24 bg-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              El Proceso
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Cómo Obtener la Certificación?
            </h2>
          </div>

          <div className="grid md:grid-cols-5 gap-6">
            {certificationProcess.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#90EE90] rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#90EE90]/20">
                  <span className="text-xl font-bold text-zinc-900">{item.step}</span>
                </div>
                <h3 className="font-bold text-white mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-zinc-400">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certificate Preview */}
      <section className="py-16 md:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
                Tu Certificación
              </span>
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900 mb-6">
                Más que un Documento, un Compromiso
              </h2>
              <p className="text-zinc-600 mb-8 leading-relaxed">
                Al obtener tu certificación "Huella Cero", recibís no solo un documento oficial, 
                sino todo un ecosistema de herramientas para comunicar tu compromiso ambiental.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: FileCheck, text: 'Certificado oficial impreso y digital' },
                  { icon: BadgeCheck, text: 'Sello verificable con QR único' },
                  { icon: BarChart3, text: 'Dashboard de impacto en tiempo real' },
                  { icon: Sparkles, text: 'Kit de marketing completo' },
                  { icon: Trophy, text: 'Reconocimiento en nuestras redes' }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#90EE90]" />
                      <span className="text-zinc-700">{item.text}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Certificate Mock */}
            <div className="relative">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-zinc-200 max-w-md mx-auto">
                <div className="text-center">
                  <div className="w-20 h-20 bg-gradient-to-br from-[#90EE90] to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Leaf className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900 mb-1">CERTIFICADO</h3>
                  <p className="text-[#14532D] font-bold text-2xl mb-4">"HUELLA CERO"</p>
                  <div className="border-t border-b border-zinc-200 py-4 my-4">
                    <p className="text-zinc-500 text-sm">Se certifica que</p>
                    <p className="text-xl font-bold text-zinc-900 my-2">[Tu Empresa]</p>
                    <p className="text-zinc-500 text-sm">ha alcanzado la neutralidad de carbono en su consumo eléctrico mediante energía solar renovable.</p>
                  </div>
                  <div className="flex justify-between items-end mt-4">
                    <div className="text-left">
                      <p className="text-xs text-zinc-400">Válido hasta</p>
                      <p className="text-sm font-bold text-zinc-700">Dic 2026</p>
                    </div>
                    <div className="w-16 h-16 bg-zinc-100 rounded-lg flex items-center justify-center">
                      <span className="text-xs text-zinc-400">QR</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Decorative badge */}
              <div className="absolute -top-4 -right-4 bg-[#90EE90] rounded-full p-4 shadow-lg">
                <Award className="w-8 h-8 text-zinc-900" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium uppercase tracking-widest text-[#90EE90] mb-4 block">
              Preguntas Frecuentes
            </span>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-zinc-900">
              Sobre la Certificación
            </h2>
          </div>

          <div className="space-y-3">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-zinc-200 rounded-xl overflow-hidden bg-white">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 text-left hover:bg-zinc-50 transition-colors"
                >
                  <span className="font-semibold text-zinc-900 pr-4 text-sm sm:text-base">{faq.question}</span>
                  <ChevronDown className={`w-5 h-5 text-zinc-500 transition-transform flex-shrink-0 ${openFaq === index ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-5">
                    <p className="text-zinc-600 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-green-800 to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-4 sm:px-6 md:px-12 text-center">
          <Award className="w-12 h-12 text-[#90EE90] mx-auto mb-6" />
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-4">
            Certificá tu Empresa Hoy
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Unite a las empresas paraguayas que lideran el cambio hacia la sustentabilidad.
          </p>
          <a
            href="https://wa.me/595991344956?text=Hola%20Zenex!%20Quiero%20certificar%20mi%20empresa%20como%20Huella%20Cero"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-white text-green-800 font-bold rounded-xl hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
          >
            <Phone className="w-5 h-5" />
            Comenzar Certificación
          </a>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default HuellaCeroPage;
